import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import {
  claimEmergency,
  getEmergencyTranscript,
  startEmergencyEncounter,
} from '../../api/emergencies.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import TriageBadge from '../../components/TriageBadge.jsx'
import TriageSummaryCard from '../../components/TriageSummaryCard.jsx'

/**
 * Doctor Emergency workspace (Phase 1c) — master / detail.
 *
 * Left: a compact list of RED cases routed to this doctor.
 * Right: the focused case — SBAR handover, the full patient transcript, and the
 * actions (Accept case / Start Encounter). No long scrolls: the detail replaces
 * the old stacked cards. On phones the two panes stack.
 *
 * The parent (DoctorDashboard) owns the `doctor-emergencies` query so the tab
 * badge and this list stay in sync; it is passed in as `query`.
 */
export default function EmergencyInbox({ query }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const rows = query?.data || []

  const [selectedId, setSelectedId] = useState(null)
  const selected = rows.find((r) => r.id === selectedId) || rows[0] || null

  // Keep a valid selection as the list refetches (a claimed case leaves it).
  useEffect(() => {
    if (rows.length && !rows.some((r) => r.id === selectedId)) {
      setSelectedId(rows[0].id)
    }
  }, [rows, selectedId])

  const claim = useMutation({
    mutationFn: (id) => claimEmergency(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doctor-emergencies'] })
      query?.refetch?.()
    },
  })

  const startEnc = useMutation({
    mutationFn: (id) => startEmergencyEncounter(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['doctor-emergencies'] })
      if (data?.encounter_id) nav(`/doctor/encounters/${data.encounter_id}`)
    },
  })

  return (
    <Card>
      <CardHeader
        title="Emergency inbox"
        subtitle="RED triage cases routed to you. Pick a case, review the AI handover, then Start Encounter to open a draft note prefilled from it."
      />
      {query?.error && <ErrorBanner error={query.error} onRetry={() => query.refetch()} />}
      {query?.isLoading && <div className="text-sm text-slate-400 py-3">Loading…</div>}
      {!query?.isLoading && rows.length === 0 && (
        <div className="text-sm text-slate-400 py-3">No emergencies waiting — you're all clear.</div>
      )}

      {rows.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
          {/* Master list */}
          <ul className="divide-y divide-slate-100 lg:border-r lg:border-slate-100 lg:pr-2 lg:max-h-[70vh] lg:overflow-auto">
            {rows.map((r) => {
              const active = selected && selected.id === r.id
              return (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedId(r.id)}
                    className={
                      'w-full text-left py-2.5 px-2 rounded transition-colors ' +
                      (active ? 'bg-rose-50 ring-1 ring-rose-200' : 'hover:bg-slate-50')
                    }
                  >
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      <span aria-hidden className="leading-none">🚨</span>
                      <span className="truncate">{r.patient_name || 'Unknown patient'}</span>
                      <TriageBadge severity={r.severity} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{r.reason}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {r.captured_at && new Date(r.captured_at).toLocaleString()}
                      {r.assigned_to_me && ' · yours'}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>

          {/* Detail panel */}
          {selected && (
            <CaseDetail
              key={selected.id}
              row={selected}
              onAccept={() => claim.mutate(selected.id)}
              onStart={() => startEnc.mutate(selected.id)}
              accepting={claim.isPending}
              starting={startEnc.isPending}
            />
          )}
        </div>
      )}

      {(claim.isError || startEnc.isError) && (
        <div className="text-xs text-rose-600 mt-2">
          {String(
            (startEnc.error || claim.error)?.response?.data?.detail ||
              'Something went wrong. Try again.'
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * The focused case: identity + actions, the SBAR handover, and the transcript.
 */
function CaseDetail({ row, onAccept, onStart, accepting, starting }) {
  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-800 flex items-center gap-2 flex-wrap">
            <span aria-hidden className="leading-none">🚨</span>
            {row.patient_name || 'Unknown patient'}
            <TriageBadge severity={row.severity} />
            {row.assigned_to_me && (
              <span className="text-[10px] uppercase tracking-wide bg-wantok-100 text-wantok-800 border border-wantok-200 rounded-full px-2 py-0.5">
                assigned to you
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {row.reason}
            {row.captured_at && <> · {new Date(row.captured_at).toLocaleString()}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {row.assigned_to_me ? (
            <span className="text-[11px] font-semibold text-wantok-700">Accepted ✓</span>
          ) : row.claimed ? (
            <span className="text-[11px] text-slate-400 italic">claimed by another</span>
          ) : (
            <button
              disabled={accepting}
              onClick={onAccept}
              className="text-xs font-semibold border border-rose-600 text-rose-700 hover:bg-rose-50 rounded px-3 py-1.5 disabled:opacity-60"
            >
              Accept case
            </button>
          )}
          <button
            disabled={starting}
            onClick={onStart}
            className="text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 disabled:opacity-60"
          >
            Start Encounter →
          </button>
        </div>
      </div>

      {row.summary && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">
            AI SBAR handover
          </div>
          <TriageSummaryCard severity={row.severity} summary={row.summary} />
        </div>
      )}

      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">
          Patient conversation
        </div>
        <CaseTranscript intentId={row.id} />
      </div>
    </div>
  )
}

/**
 * Full patient transcript for the focused case (auto-loads; keyed by case so it
 * refetches when the selection changes). PHI — only the case owner / emergency-
 * capable doctors can read it (enforced server-side).
 */
function CaseTranscript({ intentId }) {
  const q = useQuery({
    queryKey: ['emergency-transcript', intentId],
    queryFn: () => getEmergencyTranscript(intentId),
  })
  const msgs = q.data?.results || []

  return (
    <div className="max-h-72 overflow-auto rounded border border-slate-200 bg-white p-2 space-y-1">
      {q.isLoading && <div className="text-xs text-slate-400">Loading…</div>}
      {q.error && <div className="text-xs text-rose-600">Couldn't load the transcript.</div>}
      {!q.isLoading && !q.error && msgs.length === 0 && (
        <div className="text-xs text-slate-400">No messages recorded.</div>
      )}
      {msgs.map((m, i) => (
        <div key={i} className="text-xs leading-snug">
          <span
            className={
              'inline-block text-[9px] uppercase tracking-wide mr-1.5 align-top mt-0.5 ' +
              (m.role === 'user' ? 'text-wantok-700 font-bold' : 'text-slate-400 font-semibold')
            }
          >
            {m.role === 'user' ? 'Patient' : 'AI'}
          </span>
          <span className={m.role === 'user' ? 'text-slate-800' : 'text-slate-500'}>{m.content}</span>
        </div>
      ))}
    </div>
  )
}
